<template>
  <b-modal
    :visible="visible"
    modal-class="product-resources-modal"
    no-close-on-backdrop
    no-close-on-esc
    size="lg"
    @hide="close"
  >
    <template #modal-header>
      <div class="d-flex w-100 border-bottom pb-2">
        <div class="w-100 text-left">
          <h2 class="mb-1">Disponibilidade e veículos</h2>
          <p class="mb-0 text-muted">
            {{ slotLabel }}
          </p>
        </div>
        <button
          type="button"
          class="close"
          aria-label="Fechar"
          @click.prevent="close"
        >
          <span class="no no-e-remove"></span>
        </button>
      </div>
    </template>

    <div class="form-group mb-4">
      <label class="d-block mb-2 font-weight-bold" for="product-resources-product">
        Atividade
      </label>
      <b-form-select
        id="product-resources-product"
        :value="selectedProductUuid"
        :options="productSelectOptions"
        :disabled="saving"
        @change="onProductChange"
      />
    </div>

    <template v-if="selectedProductUuid">
    <div v-if="loading" class="text-center text-muted py-4">
      Carregando veículos...
    </div>

    <div v-else class="product-resources-vehicles">
      <label class="d-block mb-2 font-weight-bold" for="product-resources-product">
        Transporte
      </label>
      <div class="product-resources-list">
          <div
            v-for="vehicle in vehicles"
            :key="vehicle.uuid"
            class="product-resources-item"
          >
          <b-form-checkbox
            :checked="isSelected(vehicle.uuid)"
            :disabled="vehicleChoiceLocked(vehicle)"
            class="product-resources-option"
            @change="toggleVehicle(vehicle, $event)"
          >
            <span class="font-weight-bold">{{ vehicle.name }}</span>
            <span
              v-if="vehicle.plate || Number(vehicle.capacity) > 0"
              class="text-muted"
            >
              <template v-if="vehicle.plate"> · {{ vehicle.plate }}</template>
              <template v-if="Number(vehicle.capacity) > 0">
                · {{ vehicle.capacity }} pax
              </template>
            </span>
            <div
              v-if="!dynamicCapacity(vehicle) && busyIn(vehicle.uuid)"
              class="text-danger product-resources-description"
            >
              Em uso em {{ busyIn(vehicle.uuid) }}
            </div>
            <div
              v-else-if="usageOf(vehicle.uuid)"
              :class="[
                'product-resources-description',
                isSelected(vehicle.uuid) ? 'text-muted' : 'text-danger',
              ]"
            >
              {{ usageOf(vehicle.uuid).bookings }} reserva(s),
              {{ usageOf(vehicle.uuid).people }} pessoa(s) neste horário
              <template v-if="!isSelected(vehicle.uuid)">
                · ficarão a distribuir
              </template>
            </div>
            <div v-else class="text-muted product-resources-description">
              {{ vehicle.description }}
            </div>
          </b-form-checkbox>
          </div>
      </div>

      <p class="product-resources-capacity mb-0">
        Capacidade selecionada:
        <b>{{ selectedCapacity }} pessoas</b>
      </p>
    </div>
    </template>

    <template #modal-footer>
      <base-button type="secondary" @click="close">Cancelar</base-button>
      <base-button
        type="primary"
        :disabled="!canConfirm"
        @click="confirm"
      >
        {{ saving ? 'Salvando...' : 'Confirmar' }}
      </base-button>
    </template>
  </b-modal>
</template>

<script>
import nodeService from '@/services/nodejs'
import moment from 'moment'

export default {
  name: 'ProductResourcesModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    slotMeta: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      vehicles: [],
      selectedUuids: [],
      selectedProductUuid: '',
    }
  },

  computed: {
    productSelectOptions() {
      const products = ((this.slotMeta && this.slotMeta.products) || []).map(
        (product) => ({
          value: product.uuid,
          text: product.name,
        })
      )
      return [{ value: '', text: 'selecione a atividade' }].concat(products)
    },

    selectedProduct() {
      const products = (this.slotMeta && this.slotMeta.products) || []
      return (
        products.find((product) => product.uuid === this.selectedProductUuid) ||
        null
      )
    },

    slotLabel() {
      const slot = this.slotMeta || {}
      // "2026-10-26 13:30" -> "Seg · 26/10 · 13h30"
      const parsed = slot.block
        ? moment(slot.block, 'YYYY-MM-DD HH:mm')
        : null
      const weekday = parsed && parsed.isValid() ? parsed.format('ddd') : ''
      const weekdayLabel = weekday
        ? weekday.charAt(0).toUpperCase() + weekday.slice(1)
        : ''
      const block =
        parsed && parsed.isValid()
          ? `${weekdayLabel} · ${parsed
              .format('DD/MM · HH[h]mm')
              .replace(/h00$/, 'h')}`
          : ''
      const name = this.selectedProduct && this.selectedProduct.name
      return [name, block].filter(Boolean).join(' · ')
    },

    selectedCapacity() {
      return this.vehicles
        .filter((vehicle) => this.selectedUuids.includes(vehicle.uuid))
        .reduce((total, vehicle) => total + this.effectiveCapacity(vehicle), 0)
    },

    canConfirm() {
      if (this.saving || this.loading || !this.selectedProductUuid) return false
      return this.selectedUuids.every((uuid) => {
        const vehicle = this.vehicles.find((item) => item.uuid === uuid)
        if (!vehicle) return false
        if (this.dynamicCapacity(vehicle)) return true
        return this.effectiveCapacity(vehicle) > 0
      })
    },
  },

  watch: {
    visible(isOpen) {
      if (isOpen) this.loadVehicles()
    },
    slotMeta(slot) {
      if (!this.visible || !slot || this.loading) return
      this.applySlotSelection()
    },
  },

  methods: {
    async loadVehicles() {
      this.loading = true
      try {
        const response = await nodeService.getVehicleResources()
        const data = response && response.data ? response.data : []
        this.vehicles = Array.isArray(data) ? data : []
        this.applySlotSelection()
      } finally {
        this.loading = false
      }
    },

    applySlotSelection() {
      const slot = this.slotMeta || {}
      const products = slot.products || []
      const known = products.some((product) => product.uuid === slot.productUuid)
      this.selectedProductUuid = known ? slot.productUuid : ''
      const current = known ? slot.resources || [] : []
      this.selectedUuids = current
        .map((item) => item && item.resource_uuid)
        .filter(Boolean)
    },

    // Capacidade 0 no cadastro: vaga dinâmica (Trilha, Off-road). Não pede
    // quantidade e não entra na verificação de uso entre atrativos.
    dynamicCapacity(vehicle) {
      return Number(vehicle && vehicle.capacity) === 0
    },

    vehicleChoiceLocked(vehicle) {
      if (this.dynamicCapacity(vehicle)) return false
      return !this.isSelected(vehicle.uuid) && !!this.busyIn(vehicle.uuid)
    },

    effectiveCapacity(vehicle) {
      if (this.dynamicCapacity(vehicle)) return 0
      return Number(vehicle.capacity) || 0
    },

    onProductChange(uuid) {
      const next = uuid || ''
      if (next === this.selectedProductUuid) return
      this.selectedProductUuid = next
      this.$emit('change-product', next)
    },

    isSelected(uuid) {
      return this.selectedUuids.includes(uuid)
    },

    // "Passeio X 09:00–14:00" quando o carro está em outro atrativo num
    // horário que se sobrepõe a este (a API recusa salvar)
    busyIn(uuid) {
      const busy = (this.slotMeta && this.slotMeta.busyByUuid) || {}
      return busy[uuid] || ''
    },

    usageOf(uuid) {
      const usage = (this.slotMeta && this.slotMeta.usageByUuid) || {}
      return usage[uuid] || null
    },

    toggleVehicle(vehicle, checked) {
      const uuid = vehicle.uuid
      if (checked) {
        if (!this.selectedUuids.includes(uuid)) {
          this.selectedUuids = this.selectedUuids.concat(uuid)
        }
        return
      }
      this.selectedUuids = this.selectedUuids.filter((item) => item !== uuid)
    },

    confirm() {
      if (!this.canConfirm) return
      const slot = this.slotMeta || {}
      // O PATCH substitui o bloco: o carro que continua precisa levar o
      // motorista que já tinha, senão a API o retira (e das reservas)
      const driverByUuid = {}
      ;(slot.resources || []).forEach((item) => {
        if (item && item.driver && item.driver.uuid) {
          driverByUuid[item.resource_uuid] = { uuid: item.driver.uuid }
        }
      })
      const resources = this.vehicles
        .filter((vehicle) => this.selectedUuids.includes(vehicle.uuid))
        .map((vehicle) => ({
          resource_uuid: vehicle.uuid,
          resource_name: vehicle.name,
          resource_plate: vehicle.plate,
          resource_type: vehicle.type,
          resource_capacity: this.effectiveCapacity(vehicle),
          ...(driverByUuid[vehicle.uuid] ? { driver: driverByUuid[vehicle.uuid] } : {}),
        }))

      this.$emit('confirm', {
        block: slot.block,
        product_uuid: this.selectedProductUuid,
        resources,
      })
    },

    close() {
      if (this.saving) return
      this.$emit('close')
    },
  },
}
</script>

<style>
.product-resources-modal {
  overflow: hidden;
}

.product-resources-modal .modal-dialog {
  display: flex;
  max-height: calc(100vh - 1rem);
  max-height: calc(100dvh - 1rem);
  margin: 0.5rem auto;
}

@media (min-width: 576px) {
  .product-resources-modal .modal-dialog {
    max-height: calc(100vh - 3.5rem);
    max-height: calc(100dvh - 3.5rem);
    margin: 1.75rem auto;
  }
}

.product-resources-modal .modal-content {
  width: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
}

.product-resources-modal .modal-header,
.product-resources-modal .modal-footer {
  flex-shrink: 0;
}

.product-resources-modal .modal-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.product-resources-modal .form-group {
  flex-shrink: 0;
}

.product-resources-vehicles {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.product-resources-vehicles > label {
  flex-shrink: 0;
}

.product-resources-list {
  min-height: 0;
  overflow-y: auto;
}

.product-resources-capacity {
  flex-shrink: 0;
  margin-top: 0.75rem;
}

.product-resources-item {
  margin-bottom: 0.65rem;
}

.product-resources-item .product-resources-option {
  margin-bottom: 0;
}

.product-resources-option {
  display: block;
  margin-bottom: 0.65rem;
}

/* O tema fixa height: 1rem em .custom-checkbox .custom-control-input ~
   .custom-control-label: com descrição em duas linhas o texto passava por cima
   da opção seguinte. Mesmo seletor, prefixado, para ganhar na especificidade. */
.product-resources-option.custom-checkbox
  .custom-control-input
  ~ .custom-control-label {
  height: auto;
}

.product-resources-description {
  font-size: 0.85rem;
}
</style>
